import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { updatePageSEO } from '../utils/seoUtils';

export interface BlogSection {
  heading: string;
  body: string;
  bullets?: string[];
}

export interface BlogConfig {
  slug: string;
  title: string;
  subtitle: string;
  heroLabel: string;            // e.g. "Battery Repair"
  heroImage?: string;           // optional banner image
  intro: string;
  sections: BlogSection[];
  warrantyNote: string;         // highlighted warranty callout
  signs: string[];              // "Signs you need this repair"
  faqs: { q: string; a: string }[];
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
}

interface Props {
  blog: BlogConfig;
}

const BlogLandingPage: React.FC<Props> = ({ blog }) => {
  const navigate = useNavigate();

  useEffect(() => {
    updatePageSEO({
      title: blog.seo.title,
      description: blog.seo.description,
      keywords: blog.seo.keywords,
      canonicalUrl: `https://mintorepairs.com/${blog.slug}`,
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: blog.title,
        description: blog.seo.description,
        url: `https://mintorepairs.com/${blog.slug}`,
        publisher: {
          '@type': 'Organization',
          name: 'Minto Repairs',
          url: 'https://mintorepairs.com',
        },
      },
    });
  }, [blog]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title={blog.heroLabel} />

      {/* Hero */}
      <div className="bg-gradient-to-br from-minto-700 to-minto-500 text-white px-4 py-10">
        <div className="container mx-auto max-w-2xl text-center">
          <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
            {blog.heroLabel}
          </span>
          <h1 className="text-2xl sm:text-3xl font-bold mb-3 leading-tight">{blog.title}</h1>
          <p className="text-sm sm:text-base text-white/90 mb-6 leading-relaxed">{blog.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-white text-minto-600 rounded-full font-semibold text-sm hover:bg-gray-100 transition-colors shadow-md"
            >
              <i className="fas fa-tools mr-2"></i>Book Battery Repair
            </button>
            <a
              href="https://wa.me/+919324299258"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-green-500 text-white rounded-full font-semibold text-sm hover:bg-green-600 transition-colors shadow-md flex items-center justify-center gap-2"
            >
              <i className="fab fa-whatsapp"></i>Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-6 max-w-3xl">

        {/* Hero Image */}
        {blog.heroImage && (
          <div className="rounded-xl overflow-hidden shadow-sm">
            <img
              src={blog.heroImage}
              alt={blog.title}
              className="w-full object-cover max-h-64"
            />
          </div>
        )}

        {/* Intro */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <p className="text-sm text-gray-700 leading-relaxed">{blog.intro}</p>
        </div>

        {/* Warranty Callout */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-start gap-4">
          <div className="bg-green-500 text-white p-2.5 rounded-lg shrink-0">
            <i className="fas fa-shield-alt text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-green-900">180-Day Battery Warranty</p>
            <p className="text-xs text-green-800 mt-1 leading-relaxed">{blog.warrantyNote}</p>
          </div>
        </div>

        {/* Signs */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <i className="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
            Signs You Need a Battery Replacement
          </h2>
          <ul className="space-y-2">
            {blog.signs.map((sign, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <i className="fas fa-dot-circle text-minto-400 mt-0.5 shrink-0 text-xs"></i>
                {sign}
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigate('/')}
            className="mt-5 w-full py-3 bg-minto-500 text-white rounded-lg text-sm font-medium hover:bg-minto-600 active:bg-minto-700 transition-colors"
          >
            Check Price & Book Now
          </button>
        </div>

        {/* Blog Sections */}
        {blog.sections.map((section, i) => (
          <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.heading}</h2>
            <p className="text-sm text-gray-700 leading-relaxed">{section.body}</p>
            {section.bullets && section.bullets.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {section.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                    <i className="fas fa-check-circle text-minto-400 mt-0.5 shrink-0 text-xs"></i>
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}

        {/* How it works */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { step: '1', icon: 'fa-mobile-alt', text: 'Select your device & battery issue' },
              { step: '2', icon: 'fa-map-marker-alt', text: 'Share your Mumbai address' },
              { step: '3', icon: 'fa-motorcycle', text: 'Technician arrives at door' },
              { step: '4', icon: 'fa-check-circle', text: 'Battery replaced in ~45 mins' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="bg-minto-500 text-white w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
                  {item.step}
                </div>
                <i className={`fas ${item.icon} text-minto-400 text-lg mb-1 block`}></i>
                <p className="text-xs text-gray-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Why Customers Trust Minto</h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: 'fa-user-check', title: 'Certified Technicians', desc: 'Trained professionals, background-verified' },
              { icon: 'fa-certificate', title: 'Quality Parts', desc: 'OEM-grade battery cells, tested before install' },
              { icon: 'fa-shield-alt', title: '180-Day Warranty', desc: 'Battery covered for 180 days after replacement' },
              { icon: 'fa-rupee-sign', title: 'Transparent Pricing', desc: 'Price shown upfront — no hidden charges' },
            ].map((t) => (
              <div key={t.title} className="flex items-start gap-3">
                <div className="bg-minto-50 p-2 rounded-lg mt-0.5 shrink-0">
                  <i className={`fas ${t.icon} text-minto-500 text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-gray-100 flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1.5">
              <i className="fas fa-star text-yellow-400"></i>
              <span className="font-semibold text-gray-900">4.8/5</span> rating
            </div>
            <div className="flex items-center gap-1.5">
              <i className="fas fa-tools text-minto-500"></i>
              <span className="font-semibold text-gray-900">5,000+</span> repairs done
            </div>
            <div className="flex items-center gap-1.5">
              <i className="fas fa-clock text-minto-500"></i>
              <span className="font-semibold text-gray-900">Same day</span> service
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {blog.faqs.map((faq, i) => (
              <div key={i} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  <i className="fas fa-question-circle text-minto-400 mr-2"></i>
                  {faq.q}
                </p>
                <p className="text-sm text-gray-600 pl-5">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* WhatsApp CTA */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Not sure if you need a battery replacement?</p>
            <p className="text-xs text-gray-600 mt-0.5">Chat with us free on WhatsApp — free diagnosis in minutes</p>
          </div>
          <a
            href="https://wa.me/+919324299258"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-green-500 text-white text-sm rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center gap-2 shrink-0"
          >
            <i className="fab fa-whatsapp"></i>Chat
          </a>
        </div>

        {/* Disclaimer */}
        <div className="border border-yellow-300 bg-yellow-50 rounded-xl p-4 flex items-start gap-3">
          <i className="fas fa-exclamation-circle text-yellow-500 mt-0.5 shrink-0"></i>
          <p className="text-xs text-yellow-900 leading-relaxed">
            <span className="font-semibold">Disclaimer:</span> We are an independent third-party repair service and are not affiliated with Apple Inc. iPhone is a trademark of Apple Inc., registered in the US and other countries. All brand names and model references are used solely for identification purposes.
          </p>
        </div>

        <div className="h-16"></div>
      </div>

      <Footer />

      {/* Floating CTA */}
      <div className="fixed bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 z-50 px-4">
        <button
          onClick={() => navigate('/')}
          className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-minto-500 text-white rounded-full hover:bg-minto-600 active:bg-minto-700 transition-all duration-300 font-medium flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-lg min-w-0"
        >
          <i className="fas fa-battery-three-quarters text-base sm:text-lg"></i>
          <span className="whitespace-nowrap">Book Battery Repair</span>
        </button>
      </div>
    </div>
  );
};

export default BlogLandingPage;
